import { useState, useEffect, useCallback, useContext, createContext } from 'react';
import { getPref, setPref } from '../services/preferences';

// ─── Shape ──────────────────────────────────────────────────────────────────
export interface AppContext {
  companyId: number | null;
  /** The company's NAME, carried so a screen can show where it is without fetching the company
   *  first. The sidebar used to wait two round-trips for this and showed "HRMS PORTAL" meanwhile —
   *  on every load, for as long as the lookup took. The server names it in the first response. */
  companyName: string | null;
  branchId: number | null;
  branchCode?: string | null;
  financialYearId: number | null;
  branchName: string | null;
  financialYearName: string | null;
  fyStartDate: string | null;
  fyEndDate: string | null;
}

const EVENT_NAME = 'app-context-changed';
/** The one server preference that holds where a person last worked: GLOBAL, so it follows them into any company. */
const CONTEXT_PREF = 'context';

/**
 * The application context lives in MEMORY for the page and on the SERVER between pages.
 *
 * Company, work location and financial year used to be read out of localStorage on every API
 * request — three keys, reconciled on every read. Nothing about them is in the browser now. The
 * current values are a module variable, read synchronously by the request interceptor and by every
 * screen; every change is also written to one global preference on the server, and that preference
 * is what restores the context after a reload or on another machine. A browser wiped clean forgets
 * nothing, and two browsers agree.
 */
let current: AppContext = blank();
/** The signed-in user's own company, seeded by the auth service — the fallback when no context was chosen. */
let identityCompanyId: number | null = null;
/**
 * True while the company in memory is only the identity fallback rather than something the user
 * picked. The distinction matters exactly once, at boot: the session is rehydrated (which seeds the
 * fallback) BEFORE the saved preference is read, so without this the fallback counted as "something
 * memory already knows" and outranked the company the user had actually chosen. Selecting SV KNITS
 * and refreshing put you back on ElectEmb every time.
 */
let companyIsIdentityFallback = false;

function blank(): AppContext {
  return {
    companyId: null, companyName: null, branchId: null, branchCode: null, financialYearId: null,
    branchName: null, financialYearName: null, fyStartDate: null, fyEndDate: null,
  };
}

/** Called by the auth service whenever a session is established, so a context with no company chosen falls back to the user's own. */
export function seedContextIdentity(companyId: number | null | undefined): void {
  identityCompanyId = companyId ?? null;
  if (current.companyId == null && identityCompanyId != null) {
    current = { ...current, companyId: identityCompanyId };
    companyIsIdentityFallback = true;
    notifyContextChanged();
  }
}

/**
 * Adopt the context the SERVER resolved for this session, before anything else runs.
 *
 * This is the fix for the reload flicker, and it works because it happens FIRST. The client used to
 * seed the login company, then fetch the saved preference and correct itself — so a multi-company
 * user watched the wrong company for as long as that took, and the preference fetch itself went out
 * under it. The server already knows the answer and has already validated it against the same
 * allow-list and unit mappings that gate the headers, so there is nothing to discover and nothing
 * to correct: this is authoritative and overwrites whatever the identity seed guessed.
 *
 * Not a fallback and not a merge. A choice made LATER in this page still wins, because later means
 * the user just made it; but at boot there is no such choice, and this is it.
 */
/** Exactly what the server resolved, kept so a later restore can reassert it verbatim. */
let serverContext = {
  companyId: null as number | null,
  companyName: null as string | null,
  branchId: null as number | null,
  branchCode: null as string | null,
  branchName: null as string | null,
};

export function adoptServerContext(ctx: {
  companyId: number; companyName?: string | null;
  branchId?: number | null; branchCode?: string | null; branchName?: string | null;
} | null | undefined): void {
  if (!ctx || !(ctx.companyId > 0)) return;
  serverContext = {
    companyId: ctx.companyId,
    companyName: ctx.companyName ?? null,
    branchId: ctx.branchId ?? null,
    branchCode: ctx.branchCode ?? null,
    branchName: ctx.branchName ?? null,
  };
  current = {
    ...current,
    companyId: ctx.companyId,
    companyName: ctx.companyName ?? null,
    branchId: ctx.branchId ?? null,
    branchCode: ctx.branchCode ?? null,
    branchName: ctx.branchName ?? null,
  };
  // It came from the server, so it is not a guess about where the user is.
  companyIsIdentityFallback = false;
  contextCameFromServer = true;
  notifyContextChanged();
}

/**
 * True once the server has told us where this session belongs.
 *
 * It matters because the saved preference and the server can disagree, and only one of them has
 * checked anything. The server validates: the company is one the caller may actually access, the
 * branch one they are actually mapped to. The preference is just the last thing written from a
 * browser — it survives a mapping being revoked, a branch being deleted, a company being taken
 * away. Where the server has spoken, it wins; the preference is left to fill what the server said
 * nothing about.
 */
let contextCameFromServer = false;

/**
 * Bring back the context the server holds for this person. Run once the preference bundle has
 * loaded — at boot, and after login. Fills only what memory does not already know, so a choice made
 * on this page is never undone by an older one from the server.
 */
export function restoreContextFromPreferences(): void {
  const saved = getPref<Partial<AppContext> | null>(CONTEXT_PREF, null, 'global');
  foldInLegacyBrowserContext();
  if (saved) {
    // What memory already holds still wins — a choice made on this page must not be undone by an
    // older one from the server. The company is the exception when it is only the identity
    // fallback: that is not a choice, and letting it win is what reset the selection on every
    // refresh. A company the user actually picked is still kept.
    const chosen = Object.entries(current)
      .filter(([k, v]) => v != null && !(k === 'companyId' && companyIsIdentityFallback));
    current = {
      ...blank(),
      ...saved,
      ...Object.fromEntries(chosen),
    } as AppContext;

    // Where the server has already placed this session, the saved preference may not move it.
    // A null branch from the server is an ANSWER — "you are mapped to none here" — and the spread
    // above keeps only non-null values from memory, so a stale saved branch would otherwise win
    // and put the user back on a location the server had just refused. Company and branch are
    // restated together because a branch belongs to its company; taking one from the server and
    // the other from the preference is how they end up describing different places.
    if (contextCameFromServer) {
      current = {
        ...current,
        companyId: serverContext.companyId,
        companyName: serverContext.companyName,
        branchId: serverContext.branchId,
        branchCode: serverContext.branchCode,
        branchName: serverContext.branchName,
      };
      // A saved financial year belongs to the saved company. When the server opened the session in
      // a DIFFERENT company — one picked on the sign-in form — that year describes somewhere else,
      // so it is dropped and resolved for the company actually open.
      if (saved.companyId != null && saved.companyId !== serverContext.companyId) {
        current = { ...current, financialYearId: null, financialYearName: null, fyStartDate: null, fyEndDate: null };
      }
    }
    if (saved.companyId != null && companyIsIdentityFallback) companyIsIdentityFallback = false;
  }
  if (current.companyId == null && identityCompanyId != null) {
    current.companyId = identityCompanyId;
    companyIsIdentityFallback = true;
  }
  notifyContextChanged();
}

/**
 * The last thing this module ever reads from the browser: a context left behind by the version
 * that kept one there. Folded into memory once so nobody loses the branch they were on when the
 * upgrade landed, then deleted. Only ever fills a blank — a context already known is not overwritten.
 */
function foldInLegacyBrowserContext(): void {
  try {
    const raw = localStorage.getItem('crm_app_context');
    if (raw) {
      const legacy = JSON.parse(raw) as Partial<AppContext>;
      const known = current as unknown as Record<string, unknown>;
      const fill = Object.fromEntries(Object.entries(legacy).filter(([k, v]) => v != null && known[k] == null));
      current = { ...current, ...fill };
    }
    ['crm_app_context', 'crm_selected_unit', 'user', 'authToken', 'authExpiresAt',
     'platformExpiresAt', 'platformUser', 'platformToken'].forEach(k => localStorage.removeItem(k));
  } catch { /* blocked storage — nothing to fold in and nothing to remove */ }
}

/** Synchronous read — for use outside React (e.g. API interceptor). */
export function getAppContext(): AppContext {
  return current;
}

/** Forget everything — logout. */
export function clearAppContext(): void {
  current = blank();
  identityCompanyId = null;
  companyIsIdentityFallback = false;
  contextCameFromServer = false;
  serverContext = { companyId: null, companyName: null, branchId: null, branchCode: null, branchName: null };
  window.dispatchEvent(new Event(EVENT_NAME));
}

// ─── Transient "Full Report" toggle (Compliance reports) ────────────────────
// In-memory only (deliberately NOT persisted): when true, Compliance report
// downloads include fully-absent employees too. The API request interceptor
// forwards it as the X-Full-Report header; the backend only reads it while
// generating reports, so it is harmless on other requests. Resets to off on
// page reload.
let _fullReport = false;
export function setFullReport(on: boolean): void { _fullReport = on; }
export function getFullReport(): boolean { return _fullReport; }

/**
 * Switching company or work location takes several steps — drop the old branch, find out which
 * branches exist, pick the default — and every listener refetches on each notification. Announcing
 * the half-finished state in between meant the whole app refetched while the context held a company
 * but NO branch, and the server requires X-Branch-Id: 21-28 requests fired in the same second and
 * every one came back 400 "X-Branch-Id header is required", before the branch resolved and a second
 * round succeeded. Users saw a burst of errors on every switch.
 *
 * A transition suppresses the notifications and fires exactly ONE when it ends, so listeners only
 * ever see a context with both a company and a branch. Re-entrant, and it always re-arms in a
 * finally — a throw mid-switch must not leave the app permanently silent.
 */
let transitionDepth = 0;
let notifyPending = false;

function notifyContextChanged(): void {
  if (transitionDepth > 0) { notifyPending = true; return; }
  window.dispatchEvent(new Event(EVENT_NAME));
}

/** Dispatch a context-related event, held back while a transition is in progress. */
export function dispatchContextEvent(name: string): void {
  if (transitionDepth > 0) { notifyPending = true; return; }
  window.dispatchEvent(new Event(name));
}

/**
 * Which "era" of the context we are in. Every explicit switch bumps it.
 *
 * Company and branch are global, and half a dozen async operations write to them — the unit list,
 * the company resolution, the default-branch pick, the financial year. Each was started under some
 * company, and the answer it brings back describes THAT company. When a switch happens while one is
 * in flight, its late answer used to be applied anyway: a unit list fetched under the old company
 * does not contain the branch just chosen under the new one, so the branch was cleared a second
 * after being set — and the company followed it back. Whoever is about to write takes a reading
 * first and checks it still holds; a stale era means the answer is about a place the user has left.
 */
let contextGeneration = 0;

/** The current era — capture before an async read, check before writing what it returned. */
export function currentContextGeneration(): number { return contextGeneration; }

/** Still the era this caller started in? */
export function isContextGenerationCurrent(gen: number): boolean { return gen === contextGeneration; }

/** An explicit company/branch switch — everything in flight now describes the past. */
export function bumpContextGeneration(): void { contextGeneration++; }

/** Runs a multi-step context change as ONE announcement. */
export async function withContextTransition<T>(run: () => Promise<T>): Promise<T> {
  transitionDepth++;
  try {
    return await run();
  } finally {
    transitionDepth--;
    if (transitionDepth === 0 && notifyPending) {
      notifyPending = false;
      // The legacy names too: useAppContextState and several screens still listen on them, and a
      // switch that announced only the new name would leave those screens on stale data.
      window.dispatchEvent(new Event(EVENT_NAME));
      window.dispatchEvent(new Event('unit-changed'));
    }
  }
}

/** Write a partial update, persist it for this person, and notify listeners. */
export function setAppContext(partial: Partial<AppContext>): void {
  // A deliberate change on this page outranks where the session opened: the server's answer
  // described the start of the session, not a choice made since.
  if (partial.companyId !== undefined || partial.branchId !== undefined) contextCameFromServer = false;
  current = { ...current, ...partial };
  // Anything written through here is deliberate, so the company is no longer a fallback.
  if (partial.companyId != null) companyIsIdentityFallback = false;
  setPref(CONTEXT_PREF, current, 'global');
  notifyContextChanged();
}

// ─── React Context ──────────────────────────────────────────────────────────
const Ctx = createContext<AppContext>(blank());

export function useAppContext(): AppContext {
  const ctx = useContext(Ctx);
  return ctx;
}

/** React hook — re-renders when app context changes. */
export function useAppContextState(): AppContext {
  const [ctx, setCtx] = useState<AppContext>(() => current);

  const refresh = useCallback(() => setCtx({ ...current }), []);

  useEffect(() => {
    window.addEventListener(EVENT_NAME, refresh);
    // Also listen to legacy events for backward compat
    window.addEventListener('unit-changed', refresh);
    window.addEventListener('financial-year-updated', refresh);
    window.addEventListener('company-updated', refresh);
    return () => {
      window.removeEventListener(EVENT_NAME, refresh);
      window.removeEventListener('unit-changed', refresh);
      window.removeEventListener('financial-year-updated', refresh);
      window.removeEventListener('company-updated', refresh);
    };
  }, [refresh]);

  return ctx;
}

export { Ctx as AppContextReact };
